import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';

import { getApiAxiosInstance } from '../../../../../utils/network';
import { showErrorPopup } from '../../../../../actions/notification-actions';
import PopupV2 from '../../../../Popup/PopupV2';
import styles from '../../collegeProspectTab.scss';

const TESTID_PREFIX = 'modal.recruiting-agency';
const APPROVED_AGENCIES_ENDPOINT = '/v1/cpp/recruiting-agencies';

const RecruitingAgencyModal = ({ currentAgency, onClose, onSave }) => {
    const [agencies, setAgencies] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [agencySearch, setAgencySearch] = useState('');
    const [selectedAgency, setSelectedAgency] = useState(currentAgency);
    const [hasSelected, setHasSelected] = useState(false);
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const [isSaving, setIsSaving] = useState(false);

    const dispatch = useDispatch();
    const dropdownRef = useRef(null);

    useEffect(() => {
        const fetchAgencies = async () => {
            try {
                const response = await getApiAxiosInstance().get(APPROVED_AGENCIES_ENDPOINT);
                setAgencies(response.data?.agencies || []);
            } catch (err) {
                dispatch(showErrorPopup({
                    message: 'We couldn\'t load the list of recruiting agencies. Please try again.',
                    error: err,
                }));
                setAgencies([]);
            } finally {
                setIsLoading(false);
            }
        };
        fetchAgencies();
    }, [dispatch]);

    useEffect(() => {
        if (!isDropdownOpen) {
            return undefined;
        }
        const handleOutsideClick = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsDropdownOpen(false);
            }
        };
        document.addEventListener('mousedown', handleOutsideClick);
        return () => {
            document.removeEventListener('mousedown', handleOutsideClick);
        };
    }, [isDropdownOpen]);

    const filteredAgencies = agencySearch.trim()
        ? agencies.filter((a) => a.name.toLowerCase().includes(agencySearch.trim().toLowerCase()))
        : agencies;

    const handleToggleDropdown = () => {
        setIsDropdownOpen((prev) => !prev);
        setAgencySearch('');
    };

    const handleSelectAgency = (agency) => {
        setSelectedAgency(agency);
        setHasSelected(true);
        setIsDropdownOpen(false);
        setAgencySearch('');
    };

    const handleSave = async () => {
        setIsSaving(true);
        await onSave(selectedAgency);
        setIsSaving(false);
    };

    const triggerLabel = selectedAgency?.name ?? null;
    const searchPlaceholder = `Search ${agencies.length} agencies`;

    return (
        <PopupV2
            onCloseClick={onClose}
            config={{
                size: 'medium',
                customHeader: () => null,
            }}
        >
            <div
                className={styles.recruitingAgencyModal}
                data-testid={`${TESTID_PREFIX}.container`}
            >
                <div
                    className={styles.modalTitle}
                    data-testid={`${TESTID_PREFIX}.title`}
                >
                    Add College Recruiting Agency
                </div>

                <div
                    className={styles.agencySelectWrapper}
                    ref={dropdownRef}
                    data-testid={`${TESTID_PREFIX}.select-wrapper`}
                >
                    <button
                        type="button"
                        className={styles.agencySelectTrigger}
                        onClick={handleToggleDropdown}
                        data-testid={`${TESTID_PREFIX}.select-trigger`}
                    >
                        <span
                            className={triggerLabel ? undefined : styles.agencySelectTriggerPlaceholder}
                            data-testid={`${TESTID_PREFIX}.select-trigger.label`}
                        >
                            {triggerLabel ?? 'Select an Agency'}
                        </span>
                        <i className="fas fa-chevron-down" aria-hidden="true" />
                    </button>

                    {isDropdownOpen && (
                        <div
                            className={styles.agencyDropdownPanel}
                            data-testid={`${TESTID_PREFIX}.dropdown-panel`}
                        >
                            <div
                                className={styles.agencySearchRow}
                                data-testid={`${TESTID_PREFIX}.dropdown-search`}
                            >
                                <i className="fas fa-search" aria-hidden="true" />
                                <input
                                    type="text"
                                    className={styles.agencySearchInput}
                                    placeholder={searchPlaceholder}
                                    value={agencySearch}
                                    onChange={(e) => setAgencySearch(e.target.value)}
                                    data-testid={`${TESTID_PREFIX}.dropdown-search.input`}
                                    autoFocus
                                />
                            </div>

                            <div
                                className={styles.agencyList}
                                data-testid={`${TESTID_PREFIX}.agency-list`}
                            >
                                {isLoading && (
                                    <div
                                        className={styles.agencyLoadingText}
                                        data-testid={`${TESTID_PREFIX}.agency-list.loading`}
                                    >
                                        <i className="fas fa-circle-notch fa-spin" />
                                        <span data-testid={`${TESTID_PREFIX}.agency-list.loading.text`}>Loading...</span>
                                    </div>
                                )}
                                {!isLoading && !agencySearch.trim() && (
                                    <button
                                        type="button"
                                        className={`${styles.agencyListItem} ${!selectedAgency ? styles.agencyListItemSelected : ''}`}
                                        onClick={() => handleSelectAgency(null)}
                                        data-testid={`${TESTID_PREFIX}.agency-list.item-none`}
                                    >
                                        <span data-testid={`${TESTID_PREFIX}.agency-list.item-none.name`}>None</span>
                                    </button>
                                )}
                                {!isLoading && filteredAgencies.length === 0 && (
                                    <div
                                        className={styles.agencyNoMatches}
                                        data-testid={`${TESTID_PREFIX}.agency-list.no-matches`}
                                    >
                                        <div data-testid={`${TESTID_PREFIX}.agency-list.no-matches.line1`}>
                                            No agencies match &ldquo;{agencySearch}&rdquo;
                                        </div>
                                        <div data-testid={`${TESTID_PREFIX}.agency-list.no-matches.line2`}>
                                            Check spelling, or try a different search
                                        </div>
                                    </div>
                                )}
                                {!isLoading && filteredAgencies.map((agency) => (
                                    <button
                                        type="button"
                                        key={agency.id}
                                        className={`${styles.agencyListItem} ${selectedAgency?.id === agency.id ? styles.agencyListItemSelected : ''}`}
                                        onClick={() => handleSelectAgency(agency)}
                                        data-testid={`${TESTID_PREFIX}.agency-list.item-${agency.id}`}
                                    >
                                        <span data-testid={`${TESTID_PREFIX}.agency-list.item-${agency.id}.name`}>
                                            {agency.name}
                                        </span>
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}
                </div>

                <div
                    className={styles.modalActions}
                    data-testid={`${TESTID_PREFIX}.actions`}
                >
                    <button
                        type="button"
                        className="btn btn-default btn-sm"
                        onClick={onClose}
                        data-testid={`${TESTID_PREFIX}.cancel-btn`}
                    >
                        CANCEL
                    </button>
                    <button
                        type="button"
                        className="btn btn-primary btn-sm"
                        onClick={handleSave}
                        disabled={isSaving || (!selectedAgency && !hasSelected)}
                        data-testid={`${TESTID_PREFIX}.save-btn`}
                    >
                        SAVE
                    </button>
                </div>
            </div>
        </PopupV2>
    );
};

RecruitingAgencyModal.propTypes = {
    currentAgency: PropTypes.shape({
        id: PropTypes.number,
        name: PropTypes.string,
    }),
    onClose: PropTypes.func.isRequired,
    onSave: PropTypes.func.isRequired,
};

RecruitingAgencyModal.defaultProps = {
    currentAgency: null,
};

export default RecruitingAgencyModal;
